import { addDays, addHours, addMonths, addWeeks, setHours, startOfDay } from "date-fns";
import { BellMinus, CalendarClock, ChevronDown } from "lucide-react";
import { useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { DateTimePicker } from "@/components/ui/datetime-picker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { resolveSnoozeDate, snoozeTimeOf } from "../lib/tasks-utils";

interface TaskSnoozeDropdownProps {
  compact?: boolean;
  /** Matches neighboring submit-style buttons instead of the default outline look. */
  submit?: boolean;
  /** Ghost icon trigger — matches neighboring icon-sm ghost buttons (claim-tasks.tsx). */
  ghost?: boolean;
  onSnooze: (date: Date) => void;
  /** The task's current due date: a new day picked without a time keeps its clock. */
  currentDueDate?: Date | string | null;
}

/** The 7 presets of Task/TaskSnoozeDropdown.vue, computed at click time. */
function buildSnoozeOptions(): { label: string; date: Date }[] {
  const now = new Date();
  const today = startOfDay(now);

  return [
    { label: "Dans 3 heures", date: addHours(now, 3) },
    { label: "Demain", date: addDays(today, 1) },
    { label: "Dans 3 jours", date: addDays(today, 3) },
    { label: "Dans 1 semaine", date: addWeeks(today, 1) },
    { label: "Dans 2 semaines", date: addWeeks(today, 2) },
    { label: "Dans 6 semaines", date: addWeeks(today, 6) },
    { label: "Dans 1 mois", date: addMonths(today, 1) },
  ];
}

/** Port of Task/TaskSnoozeDropdown.vue, plus « Changer la date » for a day the presets do not offer. */
export function TaskSnoozeDropdown({
  compact = false,
  submit = false,
  ghost = false,
  onSnooze,
  currentDueDate,
}: TaskSnoozeDropdownProps) {
  // The date popover hangs off the same button as the menu it replaces, not the middle of the screen.
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [customValue, setCustomValue] = useState("");

  // The time is optional: a day alone keeps the task's current clock.
  const keptTime = snoozeTimeOf(currentDueDate);
  const customDate = resolveSnoozeDate(customValue, currentDueDate);
  // A past date is allowed: backdating a task is a correction, not a mistake to block.
  const customDateReady = customDate !== null;

  function openCustomDate() {
    setCustomValue("");
    setCustomOpen(true);
  }

  function confirmCustomDate() {
    if (!customDate || !customDateReady) {
      return;
    }
    setCustomOpen(false);
    onSnooze(customDate);
  }

  return (
    // `data-popup-open` while the date popover is up: rows that reveal their actions on hover keep
    // this button visible, like they do while the menu is open.
    <span ref={anchorRef} className="inline-flex" data-popup-open={customOpen ? "" : undefined}>
      <DropdownMenu>
        {/* Icon-only triggers get the same hover label as their neighbouring actions. */}
        <Tooltip disabled={!ghost && !compact}>
          <TooltipTrigger
            render={
              <DropdownMenuTrigger
                onClick={(event) => event.stopPropagation()}
                render={
                  ghost ? (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="text-muted-foreground"
                      aria-label="Reporter la tâche"
                    />
                  ) : (
                    <Button
                      variant={submit ? undefined : "outline"}
                      size={compact ? "sm" : "default"}
                      aria-label={compact ? "Reporter la tâche" : undefined}
                    />
                  )
                }
              />
            }
          >
            <BellMinus
              className={submit ? "text-primary-foreground" : compact ? "text-slate-500" : undefined}
            />
            {!compact && !ghost ? <span>Reporter</span> : null}
            {!compact && !ghost ? <ChevronDown className="h-3 w-3" /> : null}
          </TooltipTrigger>
          <TooltipContent>Reporter</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end" className="w-56">
          {buildSnoozeOptions().map((option) => (
            <DropdownMenuItem
              key={option.label}
              onClick={(event) => {
                event.stopPropagation();
                onSnooze(option.date);
              }}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={(event) => {
              event.stopPropagation();
              openCustomDate();
            }}
          >
            <CalendarClock />
            Changer la date…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Popover open={customOpen} onOpenChange={setCustomOpen}>
        <PopoverContent
          anchor={anchorRef}
          align="end"
          className="w-80"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex flex-col gap-3 text-left">
            <p className="text-sm font-semibold text-gray-900">Changer la date</p>
            <DateTimePicker
              value={customValue}
              onChange={setCustomValue}
              defaultDate={setHours(addDays(startOfDay(new Date()), 1), 9)}
            />
            <p className="text-xs text-gray-500">Heure facultative : sans heure, la tâche garde {keptTime}.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setCustomOpen(false)}>
                Annuler
              </Button>
              <Button size="sm" disabled={!customDateReady} onClick={confirmCustomDate}>
                Reporter
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </span>
  );
}
